import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { tap } from 'rxjs/operators';
import { RbacRolesDataSource } from '../rbac-roles/rbac-roles.component';
import { ApiRequestService } from '../../../shared/api-request.service';
import { MatSort, Sort } from '@angular/material/sort';
import { UserModel } from '../../../models/user.model';
import { UtilsService } from '../../../shared/utils.service';
import { MatPaginator } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RbacRole } from '../../../interfaces/rbac-role.interface';
import { RbacPermission } from '../../../interfaces/rbac-permission.interface';
import { RbacRolesTabsComponent } from '../rbac-roles-tabs/rbac-roles-tabs.component';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-rbac-roles-selector',
  templateUrl: './rbac-roles-selector.component.html',
  styleUrls: ['./rbac-roles-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacRolesSelectorComponent implements OnInit, OnDestroy {
  // The columns that should show in the list.
  listColumns: string[] = [
    'name',
    'rbac_permissions_count',
    'users_count',
    'date_created',
    'date_modified'
  ];

  // The list data source.
  listDataSource: RbacRolesDataSource = new RbacRolesDataSource(
    this.app,
    this.api
  );

  // The paginator and sorter.
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  // Used to check if objects should be returned instead.
  return_objects: boolean = false;

  // Selected objects from the parent component.
  selected_objects: RbacRole[] = [];

  // RBAC permission flag for the New RBAC Role button. Resolved once in the constructor.
  canCreate: boolean = false;

  // The locked partner roles the user held when the dialog opened. They stay in the returned
  // selection, so a client can neither add a partner role nor drop one through this picker.
  initiallySelectedLocked: number[] = [];
  private lockedCaptured: boolean = false;
  private lockSubscription: Subscription;
  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public dialogRef: MatDialogRef<RbacRolesSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public utils: UtilsService,
    public rbac: RBACService
  ) {
    // Resolve the RBAC permission flag. Callers gate this picker on RBAC Roles Edit, which does not
    // include Create, so the New RBAC Role button needs its own check.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_CREATE)
    ], true);

    // Update the multiple property of the data source.
    this.listDataSource.multiple =
      typeof this.data['multiple'] !== 'undefined'
        ? Boolean(this.data['multiple'])
        : false;

    // Check if we need to display the checkboxes for the selector.
    if (this.listDataSource.multiple) {
      this.listColumns.unshift('select');
    } else {
      this.listColumns.push('actions');
    }

    // Check if we need to return objects or ids.
    this.return_objects =
      typeof this.data['return_objects'] !== 'undefined'
        ? Boolean(this.data['return_objects'])
        : false;

    // Set a default selection if no selected values are passed through.
    const selected_ids: number[] = this.data['selected'] || [];

    // Apply the selected ids.
    this.listDataSource.selection.select(...selected_ids);

    // Check if we have selected objects to work with.
    this.selected_objects =
      typeof this.data['selected_objects'] !== 'undefined'
        ? this.data['selected_objects']
        : [];

    // Capture the locked partner roles of the incoming selection from the first response.
    this.lockSubscription = this.listDataSource.dataSubject.subscribe((): void => {
      // lastResponse is set before dataSubject.next(), so it is the response for this emission. The
      // guard skips the initial empty emission and every later page load.
      if (this.lockedCaptured || !this.listDataSource.lastResponse) {
        return;
      }
      const locked: number[] = this.lockedRoleIds();
      this.initiallySelectedLocked = (this.data['selected'] || []).filter((id: number): boolean => locked.includes(id));
      this.lockedCaptured = true;
    });

    // Get the data.
    this.listDataSource.getData(true);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.lockSubscription) {
      this.lockSubscription.unsubscribe();
    }
  }

  /**
   * Every partner role id of the account (from the list response) for clients, none for super admins.
   *
   * @returns {number[]}
   */
  lockedRoleIds(): number[] {
    if (!this.rbac.partnerLockApplies(true)) {
      return [];
    }
    return this.listDataSource.lastResponse?.partnerRoleIds ?? [];
  }

  /**
   * Whether a row is a partner role the current user may not select or deselect.
   *
   * @param row The role row.
   * @returns {boolean}
   */
  isLockedRow(row: RbacRole): boolean {
    return this.rbac.partnerLockApplies(row?.partner_user_id != null) || this.lockedRoleIds().includes(row?.id);
  }

  /**
   * Put the locked roles back to the state they had when the dialog opened.
   *
   * @returns {void}
   */
  private restoreLockedSelection(): void {
    this.lockedRoleIds().forEach((id: number): void => {
      if (this.initiallySelectedLocked.includes(id)) {
        this.listDataSource.selection.select(id);
      } else {
        this.listDataSource.selection.deselect(id);
      }
    });
  }

  /**
   * Select or deselect every role on the current page, leaving locked partner roles unchanged.
   *
   * @returns {void}
   */
  onToggleSelectAllOnPage(): void {
    this.listDataSource.selectAllToggle();
    this.restoreLockedSelection();
  }

  ngAfterViewInit(): void {
    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    // Define the paginator tap events.
    const _tap = tap(
      (): void => {
        this.listDataSource.limit = this.paginator.pageSize;
        this.listDataSource.offset = this.paginator.pageIndex;
        this.listDataSource.order_by = this.sort.active;
        this.listDataSource.order = this.sort.direction;
        // Refresh the list.
        this.listDataSource.getData();
      }
    );

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(_tap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap(
          (sorter: Sort): void => {
            // Sort by the selected column, not just the direction.
            this.listDataSource.order_by = sorter['active'];
            this.listDataSource.order = sorter['direction'];
            this.listDataSource.getData(true);
          }
        )
      )
      .subscribe();
  }

  /**
   * Handles the selection of multiple items in the list.
   * This can return objects or ids.
   * Returned objects will only be the ones that are selected, not all objects due to pagination unless the user clicks through all pages and select everything.
   *
   * @return {void} - Does not return anything.
   */
  onSelectMultiple(): void {
    if (this.return_objects) {
      // Return the selected objects.
      this.dialogRef.close(this.selected_objects);
    } else {
      // Return the selected ids.
      this.dialogRef.close(this.listDataSource.selection.selected);
    }
  }

  /**
   * Function to toggle row selection.
   *
   * @param {any} row - The row object to be toggled.
   * @return {void}
   */
  onToggleRowSelection(row: any): void {
    // Partner roles are managed by support.
    if (this.isLockedRow(row)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Toggle the default row selection.
    this.listDataSource.selection.toggle(row.id);
    // Check if objects should be returned.
    if (this.return_objects) {
      // Get the index of the selected object. The index will be -1 if the object is not present.
      const index: number = this.selected_objects.findIndex(
        (rbac_role: RbacRole): boolean => rbac_role.id === row.id
      );
      // Check if the row is selected and the index is not present.
      if (this.listDataSource.selection.isSelected(row.id) && index == -1) {
        // Add the row to the array.
        this.selected_objects.push(row);
      } else {
        // The row is not selected. Check if an object was found.
        if (index > -1) {
          // Remove the object if it was found.
          this.selected_objects.splice(index, 1);
        }
      }
    }
  }

  /**
   * Selects all records and closes the dialog.
   *
   * @returns {void} Returns nothing.
   */
  onSelectAllRecords(): void {
    // Return all ids from the data source, keeping locked partner roles as they were.
    const locked: number[] = this.lockedRoleIds();
    this.dialogRef.close(
      (this.listDataSource.allIds || [])
        .filter((id: number): boolean => !locked.includes(id))
        .concat(this.initiallySelectedLocked)
    );
  }

  /**
   * Deselects all records in the list data source.
   *
   * @return {void}
   */
  onDeselectAllRecords(): void {
    // Clear all selected items, then re-select the locked partner roles the user already holds.
    this.listDataSource.selection.clear();
    if (this.initiallySelectedLocked.length > 0) {
      this.listDataSource.selection.select(...this.initiallySelectedLocked);
    }
  }

  /**
   * Create a new or edit an existing RBAC role.
   *
   * @param rbac_role_id The id of the RBAC role.
   */
  onEditRbacRole(rbac_role_id?: number): void {
    // Shared create/edit method: check the permission that matches the action.
    if (rbac_role_id) {
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_EDIT)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit RBAC roles.');
        return;
      }
    } else if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create RBAC roles.');
      return;
    }

    this.utils
      .showComponentDialog(
        RbacRolesTabsComponent,
        {
          rbac_role_id: rbac_role_id
        },
        {
          width: '800px'
        }
      )
      .then(
        (): void => {
          // Refresh the list after the dialog closed.
          this.listDataSource.getData();
        }
      );
  }

  /**
   * Retrieves the contact persons of the users associated with a given RBAC role.
   *
   * @returns A string containing the contact persons of the users associated with the RBAC role, separated by new lines.
   * @param rbac_role
   */
  getRoleUsersForTooltip(rbac_role: RbacRole): string {
    return rbac_role.users
      .map(
        (user: UserModel): string => {
          return user.contact_person;
        }
      )
      .join('\n');
  }

  onClose() {
    this.dialogRef.close();
  }
}
